import { httpsCallable } from 'firebase/functions';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GlobalCardRadioButton from '../components/GlobalCardRadioButton';
import GlobalEmptyState from '../components/GlobalEmptyState';
import GlobalSidebar from '../components/GlobalSidebar';
import { GlobalSortableTh } from '../components/GlobalSortableTh';
import { GlobalButton } from '../components/GlobalButton';
import {
  ManualBankAccountForm,
  type ManualBankAccountFormValues,
} from '../components/ManualBankAccountForm';
import { PerfinaEmptyStateGraphic } from '../components/PerfinaEmptyStateGraphic';
import { StatCard } from '../components/StatCard';
import { SortableStatGrid } from '../components/SortableStatGrid';
import { buildBankAccountsKpiExplains } from '../lib/kpiExplainBuilders';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { useTableSort } from '../hooks/useTableSort';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { bankAccountKindLabel } from '../lib/bankAccount';
import { useAppData } from '../context/AppDataContext';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { functions } from '../lib/firebase';
import {
  disconnectQonto,
  disconnectQontoErrorMessage,
} from '../lib/disconnectQonto';
import { saveManualBankAccount } from '../lib/saveManualBankAccount';
import {
  startQontoOAuth,
  startQontoOAuthErrorMessage,
} from '../lib/startQontoOAuth';
import {
  syncBankTransactions,
  syncBankTransactionsErrorMessage,
} from '../lib/syncBankTransactions';
import type { BankAccount, BankConnectionStatus } from '../types';
import { formatEuro, formatMoney } from '../utils/formatters';
import { Favicon } from '../utils/favicon';
import authStyles from '../pages/auth/authPages.module.css';
import styles from './BankAccountsView.module.css';

function connectionStatusLabel(status: BankConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'error':
      return 'Error';
    case 'pending':
      return 'Pending';
    case 'disconnected':
      return 'Disconnected';
  }
}

function connectionStatusPillClass(status: BankConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'verdictPill verdictPill--invest';
    case 'error':
      return 'verdictPill verdictPill--dont';
    default:
      return 'verdictPill';
  }
}

function qontoOAuthErrorLabel(errorCode: string): string {
  switch (errorCode) {
    case 'access_denied':
      return 'Qonto authorization was cancelled.';
    case 'invalid_state':
      return 'Authorization link expired or was invalid. Try connecting again.';
    case 'missing_code':
    case 'token_exchange_failed':
    case 'missing_refresh_token':
      return 'Could not complete Qonto connection. Try again.';
    case 'server_error':
      return 'Something went wrong on our side. Try again in a moment.';
    default:
      return errorCode
        ? `Qonto connection failed (${errorCode}). Try again.`
        : 'Qonto connection failed. Try again.';
  }
}

type SyncQontoAccountsResponse = {
  accountsSynced: number;
  syncedAt: string;
};

function syncQontoAccountsErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/failed-precondition') {
      return 'Connect Qonto before syncing accounts.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to sync accounts.';
    }
    if (code === 'functions/unavailable') {
      return 'Qonto is temporarily unavailable. Try again in a moment.';
    }
    if (code === 'functions/internal' || code === 'functions/deadline-exceeded') {
      return 'Account sync failed. Try again.';
    }
  }
  return err instanceof Error ? err.message : 'Account sync failed.';
}

async function syncQontoAccounts(): Promise<SyncQontoAccountsResponse> {
  const fn = httpsCallable<void, SyncQontoAccountsResponse>(functions, 'syncQontoAccounts');
  const result = await fn();
  const accountsSynced =
    typeof result.data.accountsSynced === 'number' && Number.isFinite(result.data.accountsSynced)
      ? result.data.accountsSynced
      : 0;
  const syncedAt =
    typeof result.data.syncedAt === 'string' && result.data.syncedAt.trim()
      ? result.data.syncedAt
      : new Date().toISOString();
  return { accountsSynced, syncedAt };
}

function formatAccountSyncSuccessMessage(accountsSynced: number): string {
  const countLabel =
    accountsSynced === 1 ? '1 account' : `${accountsSynced.toLocaleString()} accounts`;
  return `Synced ${countLabel} from Qonto.`;
}

function formatSyncSuccessMessage(transactionsSynced: number, stub: boolean): string {
  const countLabel =
    transactionsSynced === 1
      ? '1 transaction'
      : `${transactionsSynced.toLocaleString()} transactions`;
  const base = `Synced ${countLabel} successfully.`;
  return stub ? `${base} (development stub data)` : base;
}

function formatSummaryDate(iso: string | undefined): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });
}

function totalBalanceClassName(total: number): string {
  if (total > 0) return 'stat-value--wrap financialSnapshot-profit financialSnapshot-profit--positive';
  if (total < 0) return 'stat-value--wrap financialSnapshot-profit financialSnapshot-profit--negative';
  return 'stat-value--wrap';
}

type BankAccountSortKey = 'name' | 'kind' | 'source' | 'identifier' | 'balance' | 'lastSync';

function formatAccountLastSync(iso: string | undefined): string {
  if (!iso) return 'Not synced yet';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Not synced yet';
  return d.toLocaleString('de-DE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function accountLastSyncAt(account: BankAccount, connectionLastSyncAt?: string): string | undefined {
  return account.balanceUpdatedAt ?? connectionLastSyncAt;
}

function balanceClassName(balance: number): string {
  if (balance > 0) return 'financialSnapshot-profit financialSnapshot-profit--positive';
  if (balance < 0) return 'financialSnapshot-profit financialSnapshot-profit--negative';
  return '';
}

function accountIdentifier(account: BankAccount): string {
  const identifier = account.accountNumber?.trim() || account.ibanMasked;
  if (identifier) return identifier;
  if (account.provider !== 'manual') return 'IBAN pending';
  return '—';
}

function accountSourceLabel(account: BankAccount): string {
  return account.provider === 'manual' ? 'Manual' : 'Qonto';
}

export function BankAccountsView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { user } = useUser();
  const { bankConnections, bankAccounts, bankLoading } = useAppData();
  const [connectSidebarOpen, setConnectSidebarOpen] = useState(false);
  const [addManualSidebarOpen, setAddManualSidebarOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [savingManualAccount, setSavingManualAccount] = useState(false);
  const [saveManualError, setSaveManualError] = useState<string | null>(null);
  const [syncingTarget, setSyncingTarget] = useState<string | 'all' | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const connectedBanner = searchParams.get('connected') === '1';
  const oauthErrorCode = searchParams.get('error');

  const chevronIcon = isDarkMode
    ? '/assets/icons/chevron.right.svg'
    : '/assets/icons/chevron.right.black.svg';

  const connectedQonto = useMemo(
    () =>
      bankConnections?.find(
        (connection) => connection.provider === 'qonto' && connection.status === 'connected'
      ) ?? null,
    [bankConnections]
  );

  const summary = useMemo(() => {
    const list = bankAccounts ?? [];
    const total = list.reduce((sum, account) => sum + (account.balance ?? 0), 0);
    const manualCount = list.filter((account) => account.provider === 'manual').length;
    const connectedCount = list.length - manualCount;

    const largest = list.reduce<(typeof list)[number] | null>((top, account) => {
      if (!top || account.balance > top.balance) return account;
      return top;
    }, null);

    const syncTimes = [
      ...list.map((account) => account.balanceUpdatedAt),
      ...(bankConnections ?? []).map((connection) => connection.lastSyncAt),
    ]
      .filter((value): value is string => Boolean(value))
      .map((value) => new Date(value).getTime())
      .filter((value) => !Number.isNaN(value));
    const lastUpdated =
      syncTimes.length > 0 ? new Date(Math.max(...syncTimes)).toISOString() : undefined;

    return {
      total,
      count: list.length,
      manualCount,
      connectedCount,
      largest,
      lastUpdated,
    };
  }, [bankAccounts, bankConnections]);

  const kpiExplains = useMemo(
    () => buildBankAccountsKpiExplains(bankAccounts ?? [], summary, formatSummaryDate),
    [bankAccounts, summary],
  );

  const syncing = syncingTarget !== null;

  const oauthErrorBanner = useMemo(() => {
    if (!oauthErrorCode) return null;
    return qontoOAuthErrorLabel(oauthErrorCode);
  }, [oauthErrorCode]);

  const clearOAuthQueryParams = useCallback(() => {
    if (!connectedBanner && !oauthErrorCode) return;
    const next = new URLSearchParams(searchParams);
    next.delete('connected');
    next.delete('error');
    setSearchParams(next, { replace: true });
  }, [connectedBanner, oauthErrorCode, searchParams, setSearchParams]);

  const openConnectSidebar = useCallback(() => {
    setConnectError(null);
    clearOAuthQueryParams();
    setConnectSidebarOpen(true);
  }, [clearOAuthQueryParams]);

  const openAddManualSidebar = useCallback(() => {
    setSaveManualError(null);
    setAddManualSidebarOpen(true);
  }, []);

  const closeAddManualSidebar = useCallback(() => {
    if (savingManualAccount) return;
    setAddManualSidebarOpen(false);
    setSaveManualError(null);
  }, [savingManualAccount]);

  async function handleSaveManualAccount(values: ManualBankAccountFormValues) {
    if (!user?.uid || savingManualAccount) return;

    setSaveManualError(null);
    setSavingManualAccount(true);

    try {
      await saveManualBankAccount(user.uid, values);
      setAddManualSidebarOpen(false);
    } catch (err) {
      setSaveManualError(err instanceof Error ? err.message : 'Failed to save bank account.');
    } finally {
      setSavingManualAccount(false);
    }
  }

  async function handleConnectQonto() {
    if (connecting) return;
    setConnectError(null);
    clearOAuthQueryParams();
    setConnecting(true);
    try {
      const { authorizationUrl } = await startQontoOAuth();
      window.location.assign(authorizationUrl);
    } catch (err) {
      setConnectError(startQontoOAuthErrorMessage(err));
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    if (disconnecting) return;

    setDisconnectError(null);
    setDisconnecting(true);

    try {
      await disconnectQonto();
      setDisconnectConfirmOpen(false);
      setSyncSuccess(null);
      setSyncError(null);
      clearOAuthQueryParams();
    } catch (err) {
      setDisconnectError(disconnectQontoErrorMessage(err));
    } finally {
      setDisconnecting(false);
    }
  }

  function handleSelectProvider(provider: string) {
    if (provider === 'qonto') {
      void handleConnectQonto();
    }
  }

  async function handleSync(bankAccountId?: string) {
    if (syncing) return;

    setSyncError(null);
    setSyncSuccess(null);
    setSyncingTarget(bankAccountId ?? 'all');

    try {
      if (!bankAccountId && accounts.length === 0) {
        const accountResult = await syncQontoAccounts();
        if (accountResult.accountsSynced === 0) {
          setSyncSuccess(
            'Sync finished, but Qonto did not return any accounts yet. Try again in a moment.'
          );
          return;
        }

        const txResult = await syncBankTransactions();
        setSyncSuccess(
          `${formatAccountSyncSuccessMessage(accountResult.accountsSynced)} ${formatSyncSuccessMessage(
            txResult.transactionsSynced,
            txResult.stub === true
          )}`
        );
        return;
      }

      const result = await syncBankTransactions(bankAccountId);
      setSyncSuccess(formatSyncSuccessMessage(result.transactionsSynced, result.stub === true));
    } catch (err) {
      if (!bankAccountId && accounts.length === 0) {
        setSyncError(syncQontoAccountsErrorMessage(err));
      } else {
        setSyncError(syncBankTransactionsErrorMessage(err));
      }
    } finally {
      setSyncingTarget(null);
    }
  }

  const accounts = useMemo(() => bankAccounts ?? [], [bankAccounts]);
  const hasAccounts = accounts.length > 0;

  const { sortKey, sortDirection, toggleSort, sortRows } = useTableSort<BankAccountSortKey>(
    'balance',
    'desc',
  );

  const sortedAccounts = useMemo(() => {
    return sortRows(accounts, {
      name: (account) => account.name,
      kind: (account) => bankAccountKindLabel(account.kind),
      source: (account) => accountSourceLabel(account),
      identifier: (account) => accountIdentifier(account),
      balance: (account) => (Number.isFinite(account.balance) ? account.balance : 0),
      lastSync: (account) => {
        const lastSyncAt = accountLastSyncAt(account, connectedQonto?.lastSyncAt);
        return lastSyncAt ? new Date(lastSyncAt).getTime() || 0 : 0;
      },
    });
  }, [accounts, connectedQonto?.lastSyncAt, sortRows]);

  const plusIcon = (
    <img
      src={
        isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'
      }
      alt=""
      width={16}
      height={16}
    />
  );

  const toolbarActions = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        className={styles.tapTarget}
        disabled={savingManualAccount}
        onClick={openAddManualSidebar}
        icon={plusIcon}
      >
        Add bank account
      </GlobalButton>
      {connectedQonto ? (
        <GlobalButton
          type="button"
          variant="primary"
          className={styles.tapTarget}
          isLoading={syncingTarget === 'all'}
          disabled={syncing}
          onClick={() => handleSync()}
        >
          Sync now
        </GlobalButton>
      ) : (
        <GlobalButton
          type="button"
          variant="secondary"
          className={styles.tapTarget}
          disabled={connecting}
          isLoading={connecting}
          onClick={openConnectSidebar}
        >
          Connect account
        </GlobalButton>
      )}
    </>
  );

  const summarySection =
    !bankLoading && hasAccounts ? (
      <>
        <ViewSectionHeader
          eyebrow="Banking"
          title="Summary"
          subtitle="Combined balances across all of your bank accounts."
        />
        <SortableStatGrid
          viewId="bank-accounts"
          className="grid-stats grid-stats-2"
          ariaLabel="Bank accounts summary"
          cards={[
            {
              id: 'total-balance',
              pinned: true,
              label: 'Total balance',
              node: (
                <StatCard
                  netWorthHeadline
                  label="Total balance"
                  emoji="🏦"
                  value={
                    <span className={totalBalanceClassName(summary.total)}>
                      {formatEuro(summary.total)}
                    </span>
                  }
                  hint={`Across ${summary.count} ${summary.count === 1 ? 'account' : 'accounts'}`}
                  kpiExplain={kpiExplains['total-balance']}
                />
              ),
            },
            {
              id: 'accounts',
              label: 'Accounts',
              node: (
                <StatCard
                  label="Accounts"
                  emoji="📂"
                  value={String(summary.count)}
                  hint={`${summary.connectedCount} connected · ${summary.manualCount} manual`}
                  kpiExplain={kpiExplains.accounts}
                />
              ),
            },
            {
              id: 'largest-account',
              label: 'Largest account',
              node: (
                <StatCard
                  label="Largest account"
                  emoji="📊"
                  value={
                    summary.largest ? (
                      <span className={totalBalanceClassName(summary.largest.balance)}>
                        {formatMoney(summary.largest.balance, summary.largest.currency)}
                      </span>
                    ) : (
                      '—'
                    )
                  }
                  hint={summary.largest ? summary.largest.name : 'No accounts yet'}
                  valueWrap
                  kpiExplain={kpiExplains['largest-account']}
                />
              ),
            },
            {
              id: 'last-updated',
              label: 'Last updated',
              node: (
                <StatCard
                  label="Last updated"
                  emoji="🕒"
                  value={formatSummaryDate(summary.lastUpdated)}
                  hint="Most recent balance update"
                  valueWrap
                  kpiExplain={kpiExplains['last-updated']}
                />
              ),
            },
          ]}
        />
      </>
    ) : null;

  const accountsSection = (
    <>
      <ViewSectionHeader
        eyebrow="Banking"
        title="Bank accounts"
        subtitle="Add accounts manually or connect a bank to sync balances and transactions."
      />

      <section className={styles.root}>
        {connectedBanner ? (
          <div
            className={`${authStyles.banner} ${authStyles.bannerSuccess} ${styles.banner}`}
            role="status"
          >
            Qonto connected successfully. Use Sync now to pull transactions.
          </div>
        ) : null}

      {oauthErrorBanner ? (
        <div
          className={`${authStyles.banner} ${authStyles.bannerError} ${styles.banner}`}
          role="alert"
        >
          {oauthErrorBanner}
        </div>
      ) : null}

      {connectError ? (
        <div
          className={`${authStyles.banner} ${authStyles.bannerError} ${styles.banner}`}
          role="alert"
        >
          {connectError}
        </div>
      ) : null}

      {syncSuccess ? (
        <div
          className={`${authStyles.banner} ${authStyles.bannerSuccess} ${styles.banner}`}
          role="status"
        >
          {syncSuccess}
        </div>
      ) : null}

      {syncError ? (
        <div
          className={`${authStyles.banner} ${authStyles.bannerError} ${styles.banner}`}
          role="alert"
        >
          <p style={{ margin: 0 }}>{syncError}</p>
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            className={styles.tapTarget}
            disabled={syncing}
            isLoading={syncingTarget === 'all'}
            onClick={() => handleSync()}
            style={{ marginTop: 10 }}
          >
            Try again
          </GlobalButton>
        </div>
      ) : null}

      {disconnectError ? (
        <div
          className={`${authStyles.banner} ${authStyles.bannerError} ${styles.banner}`}
          role="alert"
        >
          {disconnectError}
        </div>
      ) : null}

      {bankLoading ? (
        <p className="muted">Loading bank data…</p>
      ) : connectedQonto || hasAccounts ? (
        <>
          {connectedQonto ? (
          <section
            aria-label="Qonto connection"
            className={`table-card ${styles.connectionPanel}`}
          >
            <div className={styles.connectionHeader}>
              <div>
                <h2 className={styles.connectionTitle}>
                  {connectedQonto.organizationName ?? 'Qonto'}
                </h2>
                <span className={connectionStatusPillClass(connectedQonto.status)}>
                  {connectionStatusLabel(connectedQonto.status)}
                </span>
              </div>
              {!disconnectConfirmOpen ? (
                <GlobalButton
                  type="button"
                  variant="secondary"
                  destructive
                  className={styles.tapTarget}
                  disabled={disconnecting || syncing}
                  onClick={() => setDisconnectConfirmOpen(true)}
                >
                  Disconnect
                </GlobalButton>
              ) : (
                <div className={styles.connectionActions}>
                  <p className={`muted ${styles.disconnectConfirmText}`}>Are you sure?</p>
                  <GlobalButton
                    type="button"
                    variant="secondary"
                    destructive
                    className={styles.tapTarget}
                    isLoading={disconnecting}
                    disabled={disconnecting}
                    onClick={() => handleDisconnect()}
                  >
                    Confirm disconnect
                  </GlobalButton>
                  <GlobalButton
                    type="button"
                    variant="secondary"
                    className={styles.tapTarget}
                    disabled={disconnecting}
                    onClick={() => setDisconnectConfirmOpen(false)}
                  >
                    Cancel
                  </GlobalButton>
                </div>
              )}
            </div>
          </section>
          ) : null}
          {hasAccounts ? (
            <div className="table-card">
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <GlobalSortableTh sortKey="name" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Account
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="kind" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Type
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="identifier" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        IBAN / number
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="source" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Source
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="balance" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Balance
                      </GlobalSortableTh>
                      <GlobalSortableTh sortKey="lastSync" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                        Last updated
                      </GlobalSortableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAccounts.map((account) => {
                      const lastSyncAt = accountLastSyncAt(account, connectedQonto?.lastSyncAt);
                      return (
                        <tr
                          key={account.id}
                          className="click-row"
                          onClick={() => navigate(`/bank-accounts/${account.id}`)}
                        >
                          <td>
                            <div className="companyCell">
                              {account.websiteUrl ? (
                                <Favicon
                                  websiteUrl={account.websiteUrl}
                                  size={20}
                                  alt=""
                                  className="companyFavicon"
                                />
                              ) : (
                                <span
                                  className="companyFavicon"
                                  style={{
                                    display: 'inline-block',
                                    width: 20,
                                    height: 20,
                                    borderRadius: 6,
                                    background: 'var(--global-separator-color)',
                                  }}
                                  aria-hidden
                                />
                              )}
                              <span>{account.name}</span>
                            </div>
                          </td>
                          <td>{bankAccountKindLabel(account.kind)}</td>
                          <td>{accountIdentifier(account)}</td>
                          <td>
                            <span className="muted">{accountSourceLabel(account)}</span>
                          </td>
                          <td>
                            <span className={balanceClassName(account.balance)}>
                              {formatMoney(account.balance, account.currency)}
                            </span>
                            {account.currency !== 'EUR' ? (
                              <span className="muted" style={{ marginLeft: 6 }}>
                                {account.currency}
                              </span>
                            ) : null}
                          </td>
                          <td className="muted">
                            {account.provider === 'manual'
                              ? lastSyncAt
                                ? formatAccountLastSync(lastSyncAt)
                                : 'Manually tracked'
                              : formatAccountLastSync(lastSyncAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : connectedQonto ? (
            <section aria-label="Waiting for bank accounts" className="empty-state">
              <PerfinaEmptyStateGraphic />
              <h2 className={styles.emptyConnectedTitle}>Waiting for accounts</h2>
              <p className={`muted ${styles.emptyConnectedCopy}`}>
                Qonto is connected. Your accounts can take a moment to appear after the first sync
                — tap Sync now to fetch balances and IBANs from Qonto.
              </p>
              <GlobalButton
                type="button"
                variant="primary"
                className={styles.tapTarget}
                isLoading={syncingTarget === 'all'}
                disabled={syncing}
                onClick={() => handleSync()}
              >
                Sync now
              </GlobalButton>
            </section>
          ) : null}
        </>
      ) : (
        <GlobalEmptyState
          buttonText="Add bank account"
          onButtonClick={openAddManualSidebar}
          buttonIcon={plusIcon}
        />
      )}
      </section>
    </>
  );

  const sections: ViewSection[] = [
    ...(summarySection ? [{ id: 'summary', label: 'Summary', content: summarySection }] : []),
    { id: 'accounts', label: 'Bank accounts', content: accountsSection },
  ];

  return (
    <ViewPage title="Bank accounts" actions={toolbarActions}>
      <ViewSections mode={layoutMode} sections={sections} />

      <GlobalSidebar
        isOpen={addManualSidebarOpen}
        onClose={closeAddManualSidebar}
      >
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Add bank account</h2>
          <p className="bankProviderConnectIntro">
            Track a bank account manually with a name, website, and balance. Manual accounts do not
            sync transactions.
          </p>
          <ManualBankAccountForm
            saving={savingManualAccount}
            error={saveManualError}
            onSubmit={handleSaveManualAccount}
            onCancel={closeAddManualSidebar}
          />
        </div>
      </GlobalSidebar>

      <GlobalSidebar
        isOpen={connectSidebarOpen}
        onClose={() => {
          if (!connecting) setConnectSidebarOpen(false);
        }}
      >
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">Connect account</h2>
          <p className="bankProviderConnectIntro">
            Choose a banking partner to securely link your accounts. You will be redirected to sign
            in with the provider.
          </p>
          {connectError ? <p className="sidebarFormError">{connectError}</p> : null}
          <div className="bankProviderConnectGrid" role="radiogroup" aria-label="Banking partners">
            <div className="bankProviderCard--qonto">
              <GlobalCardRadioButton
                name="bank-provider"
                value="qonto"
                isSelected={connecting}
                onSelect={handleSelectProvider}
                icon={
                  <img
                    src="/assets/graphics/qonto-logo.svg"
                    alt=""
                    className="bankProviderCardLogo"
                  />
                }
                headline="Qonto"
                paragraph="Business banking for startups and SMEs. Sync balances and transactions in real time."
                ctaLabel={connecting ? 'Redirecting…' : 'Connect'}
                ctaIcon={<img src={chevronIcon} alt="" width={12} height={12} />}
              />
            </div>
          </div>
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
